// Runtime logger that can be controlled in production
let isLoggingEnabled: boolean | null = null;

// Check if logging should be enabled (runtime detection)
function shouldLog(): boolean {
  if (isLoggingEnabled !== null) {
    return isLoggingEnabled;
  }

  // Check localStorage for override
  if (typeof window !== 'undefined') {
    const override = localStorage.getItem('VIBESHIP_DEBUG');
    if (override === 'true') {
      isLoggingEnabled = true;
      return true;
    }
    if (override === 'false') {
      isLoggingEnabled = false;
      return false;
    }
  }

  // Default: only log on localhost
  if (typeof window !== 'undefined') {
    isLoggingEnabled = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.startsWith('192.168.');
  } else {
    isLoggingEnabled = false;
  }

  return isLoggingEnabled;
}

// Track logged messages to avoid duplicates
const loggedMessages = new Set<string>();
let lastLogTime = 0;
const LOG_THROTTLE_MS = 100; // Throttle identical messages within 100ms

export const logger = {
  log: (...args: any[]) => {
    if (!shouldLog()) return;
    
    // Create a key for deduplication
    const key = JSON.stringify(args);
    const now = Date.now();
    
    // Skip if same message was logged recently
    if (loggedMessages.has(key) && (now - lastLogTime) < LOG_THROTTLE_MS) {
      return;
    }
    
    loggedMessages.add(key);
    lastLogTime = now;
    
    // Clear old messages after delay
    setTimeout(() => loggedMessages.delete(key), LOG_THROTTLE_MS);
    
    console.log(...args);
  },
  
  warn: (...args: any[]) => {
    if (!shouldLog()) return;
    console.warn(...args);
  },
  
  error: (...args: any[]) => {
    // Always log errors
    console.error(...args);
  },
  
  debug: (...args: any[]) => {
    if (!shouldLog()) return;
    const debugEnabled = typeof window !== 'undefined' && 
                        localStorage.getItem('VIBESHIP_DEBUG_VERBOSE') === 'true';
    if (debugEnabled) {
      console.log('[DEBUG]', ...args);
    }
  },

  // Initialize logger with a welcome message
  init: () => {
    if (!shouldLog()) return;
    console.log(
      '%c🚀 Vibeship DevTools v1.0.61',
      'color: #6366f1; font-weight: bold; font-size: 14px;',
      '\n\nLogging is enabled. To disable, run:\nlocalStorage.setItem("VIBESHIP_DEBUG", "false")\n\nFor verbose logging:\nlocalStorage.setItem("VIBESHIP_DEBUG_VERBOSE", "true")'
    );
  },

  // Control logging at runtime
  enable: () => {
    isLoggingEnabled = true;
    if (typeof window !== 'undefined') {
      localStorage.setItem('VIBESHIP_DEBUG', 'true');
    }
    console.log('Vibeship DevTools logging enabled');
  },

  disable: () => {
    isLoggingEnabled = false;
    if (typeof window !== 'undefined') {
      localStorage.setItem('VIBESHIP_DEBUG', 'false');
    }
    console.log('Vibeship DevTools logging disabled');
  }
};